import { defineComponent, defineAsyncComponent } from 'vue';
import { QBanner, QBtn, QCheckbox, QIcon, QItem, QItemLabel, QItemSection, QList, QMenu } from 'quasar';
import { appSettings } from '@/Shared/appSettings';
import utils from '../../../Shared/utils.jsx';
import careHelpfulFunctions from '@/Application/careHelpfulFunctions.jsx';
import ControlDesignerScopeSummaryField from './controlDesignerScopeSummaryField.jsx';
import ControlDesignerScopeIncludesEditor from './controlDesignerScopeIncludesEditor.jsx';
import {
    formatTruncatedSelection,
    listScopeScriptMembers,
} from './controlDesignerScopeScript.js';
import {
    configureScopeScriptEditorSuggestOptions,
    createScopeScriptCompletionProvider,
    filterMembersByPublic,
    listEmbeddedUserControlsInDesign,
} from './controlDesignerScopeScriptCompletion.js';
import { createScopeScriptSignatureHelpProvider } from './controlDesignerScopeScriptSignatureHelp.js';
import { listCallableMembersFromObject } from './controlDesignerScopeScriptRuntimeMembers.js';
import { hasLegacyScopeItems, isScopeScriptUnset } from './controlDesignerScopeScriptMigrate.js';

const MonacoEditor = defineAsyncComponent(() => import('../3rdParty/MonacoEditor.jsx'));

function normalizePublicSelection(scopePublic, members) {
    const validNames = new Set(members.map((member) => member.name));
    return (scopePublic || []).filter((name) => validNames.has(name));
}

/**
 * ScopeScript editor for Control Designer dock tab. Defers Monaco mount until the tab is active
 * and the host element is in the document (avoids empty editor on inactive dock panels).
 * Commits to the parent on blur via change-content (avoids remount/focus loss on each keystroke).
 */
export default defineComponent({
    name: 'ControlDesignerCodePanel',
    components: {
        MonacoEditor,
        ControlDesignerScopeSummaryField,
        ControlDesignerScopeIncludesEditor,
        QBanner,
        QBtn,
        QCheckbox,
        QIcon,
        QItem,
        QItemLabel,
        QItemSection,
        QList,
        QMenu,
    },
    props: {
        active: {
            type: Boolean,
            default: false,
        },
        content: {
            type: String,
            default: '',
        },
        scopeIncludes: {
            type: Array,
            default: () => [],
        },
        scopePublic: {
            type: Array,
            default: () => [],
        },
        variables: {
            type: Array,
            default: () => [],
        },
        functions: {
            type: Array,
            default: () => [],
        },
        legacyMigrationPending: {
            type: Boolean,
            default: false,
        },
        mountKey: {
            type: Number,
            default: 0,
        },
        designRoot: {
            type: Object,
            default: null,
        },
        actionSet: {
            type: Array,
            default: () => [],
        },
        resolveActionSet: {
            type: Function,
            default: null,
        },
        commands: {
            type: Array,
            default: () => [],
        },
        resolveCommands: {
            type: Function,
            default: null,
        },
        controlDataSchema: {
            type: Object,
            default: null,
        },
    },
    emits: ['change-content', 'change-scope-includes', 'change-scope-public', 'migrate-legacy-scope', 'add-on-created', 'add-on-mounted'],
    data() {
        return {
            showEditor: false,
            includesPopupOpen: false,
            publicPopupOpen: false,
            includesValidationErrors: [],
            libraryMembersByAlias: {},
            libraryScopeScriptByAlias: {},
            embeddedUserControlMembersByName: {},
            embeddedUserControlScopeScriptByName: {},
            utilDotCompletions: null,
            _completionProvider: null,
            _signatureHelpProvider: null,
            _monacoModel: null,
        };
    },
    computed: {
        scopeMembers() {
            return listScopeScriptMembers(this.content);
        },
        includesDisplayText() {
            const labels = (this.scopeIncludes || []).map((item) => item?.As).filter(Boolean);
            return formatTruncatedSelection(labels);
        },
        publicDisplayText() {
            const selected = new Set(normalizePublicSelection(this.scopePublic, this.scopeMembers));
            const labels = this.scopeMembers
                .filter((member) => selected.has(member.name))
                .map((member) => member.name);
            return formatTruncatedSelection(labels);
        },
        normalizedScopePublic() {
            return normalizePublicSelection(this.scopePublic, this.scopeMembers);
        },
        includesBlockClose() {
            return this.includesValidationErrors.length > 0;
        },
        showLegacyMigrateMenu() {
            return hasLegacyScopeItems(this.variables, this.functions);
        },
        showLegacyMigrationGate() {
            return this.legacyMigrationPending
                || (hasLegacyScopeItems(this.variables, this.functions)
                    && isScopeScriptUnset({ ScopeScript: this.content }));
        },
        showLegacyCompatibilityWarning() {
            return appSettings.LegacyCompatibility === true;
        },
    },
    watch: {
        active: {
            immediate: true,
            handler(active, prevActive) {
                if (prevActive && !active)
                    this.flushEditorContentToParent();
                this.scheduleEditorMount(active);
            },
        },
        mountKey() {
            this.scheduleEditorMount(this.active, true);
        },
        legacyMigrationPending() {
            this.scheduleEditorMount(this.active);
        },
        content() {
            this.pruneScopePublicIfNeeded();
        },
        scopeIncludes: {
            deep: true,
            handler() {
                this.refreshLibraryMembersCache();
            },
        },
        designRoot: {
            deep: true,
            handler() {
                this.refreshEmbeddedUserControlMembersCache();
            },
        },
    },
    beforeUnmount() {
        this.disposeCompletionProvider();
        if (this._mountTimer != null) {
            clearTimeout(this._mountTimer);
            this._mountTimer = null;
        }
    },
    methods: {
        navigateToLine(lineNumber, column) {
            if (lineNumber == null)
                return;

            const tryNavigate = (attemptsLeft) => {
                if (this.$refs.monacoEditor?.gotoLine) {
                    this.$refs.monacoEditor.gotoLine(lineNumber, column);
                    this.focusEditor();
                    return;
                }
                if (attemptsLeft > 0)
                    setTimeout(() => tryNavigate(attemptsLeft - 1), 0);
            };

            this.$nextTick(() => {
                tryNavigate(8);
            });
        },
        focusEditor() {
            this.$refs.monacoEditor?.editor?.focus?.();
        },
        flushEditorContentToParent() {
            if (!this.showEditor || this.showLegacyMigrationGate)
                return;
            const text = this.$refs.monacoEditor?.getContent?.();
            if (text != null)
                this.$emit('change-content', text);
        },
        scheduleEditorMount(active, forceRemount = false) {
            if (this._mountTimer != null) {
                clearTimeout(this._mountTimer);
                this._mountTimer = null;
            }

            if (!active) {
                this.showEditor = false;
                return;
            }

            if (this.showLegacyMigrationGate) {
                this.flushEditorContentToParent();
                this.showEditor = false;
                return;
            }

            if (!forceRemount && this.showEditor && this.$refs.monacoEditor?.editor)
                return;

            this.flushEditorContentToParent();
            this.showEditor = false;
            this._mountTimer = setTimeout(() => {
                this._mountTimer = null;
                requestAnimationFrame(() => {
                    if (this.active && !this.showLegacyMigrationGate)
                        this.showEditor = true;
                });
            }, 0);
        },
        onScopeIncludesChanged(includes) {
            this.$emit('change-scope-includes', includes);
        },
        onIncludesValidationChange(errors) {
            this.includesValidationErrors = errors || [];
        },
        onIncludesPopupClose() {
            this.includesPopupOpen = false;
        },
        onIncludesBeforeClose() {
            this.$refs.includesEditor?.cancelDraft?.();
        },
        onPublicPopupOpen(value) {
            this.publicPopupOpen = value;
            if (value)
                this.pruneScopePublicIfNeeded();
        },
        pruneScopePublicIfNeeded() {
            const normalized = normalizePublicSelection(this.scopePublic, this.scopeMembers);
            if (JSON.stringify(normalized) !== JSON.stringify(this.scopePublic || []))
                this.$emit('change-scope-public', normalized);
        },
        isPublicMemberSelected(name) {
            return this.normalizedScopePublic.includes(name);
        },
        togglePublicMember(name, selected) {
            const current = new Set(this.normalizedScopePublic);
            if (selected)
                current.add(name);
            else
                current.delete(name);

            this.$emit('change-scope-public', [...current].sort());
        },
        onMigrateLegacyScopeClick() {
            this.$emit('migrate-legacy-scope');
        },
        addOnCreated() {
            this.$emit('add-on-created');
        },
        addOnMounted() {
            this.$emit('add-on-mounted');
        },

        disposeCompletionProvider() {
            if (this._completionProvider) {
                this._completionProvider.dispose();
                this._completionProvider = null;
            }
            if (this._signatureHelpProvider) {
                this._signatureHelpProvider.dispose();
                this._signatureHelpProvider = null;
            }
            this._monacoModel = null;
        },
        getUtilDotCompletions() {
            if (!this.utilDotCompletions)
                this.utilDotCompletions = listCallableMembersFromObject(careHelpfulFunctions, 'util');

            return this.utilDotCompletions;
        },
        onMonacoInit(_editor, monaco) {
            this.disposeCompletionProvider();

            const model = this.$refs.monacoEditor?.model;
            if (!model || !monaco)
                return;

            configureScopeScriptEditorSuggestOptions(_editor);

            this._monacoModel = model;
            this.refreshLibraryMembersCache();
            this.refreshEmbeddedUserControlMembersCache();

            this._completionProvider = createScopeScriptCompletionProvider({
                monaco,
                model,
                getDesignRoot: () => this.designRoot,
                getScopeScriptText: () => this.$refs.monacoEditor?.getContent?.() ?? this.content,
                getCommands: () => this.resolveCommands?.() ?? this.commands,
                getControlDataSchema: () => this.controlDataSchema,
                getUtilDotCompletions: () => this.getUtilDotCompletions(),
                getEmbeddedUserControlMembersByName: () => this.embeddedUserControlMembersByName,
                getLibraryMembersByAlias: () => this.libraryMembersByAlias,
                getScopeIncludes: () => this.scopeIncludes,
            });

            this._signatureHelpProvider = createScopeScriptSignatureHelpProvider({
                monaco,
                model,
                getDesignRoot: () => this.designRoot,
                getScopeScriptText: () => this.$refs.monacoEditor?.getContent?.() ?? this.content,
                getCommands: () => this.resolveCommands?.() ?? this.commands,
                getApiFunction: (name) => utils.apiWrapper?.[name],
                getUtilFunction: (name) => careHelpfulFunctions?.[name],
                getEmbeddedUserControlMembersByName: () => this.embeddedUserControlMembersByName,
                getEmbeddedUserControlScopeScriptByName: (name) => this.embeddedUserControlScopeScriptByName?.[name] || '',
                getLibraryMembersByAlias: () => this.libraryMembersByAlias,
                getLibraryScopeScriptByAlias: (alias) => this.libraryScopeScriptByAlias?.[alias] || '',
                getScopeIncludes: () => this.scopeIncludes,
            });
        },
        async refreshLibraryMembersCache() {
            const includes = this.scopeIncludes || [];
            if (!includes.length) {
                this.libraryMembersByAlias = {};
                this.libraryScopeScriptByAlias = {};
                return;
            }

            const next = {};
            const nextScopeScripts = {};

            await Promise.all(includes.map(async (inc) => {
                const alias = String(inc?.As || '').trim();
                const path = String(inc?.Path || '').trim();
                if (!alias || !path) {
                    if (alias) {
                        next[alias] = [];
                        nextScopeScripts[alias] = '';
                    }
                    return;
                }

                try {
                    const cached = await utils.scopeLibrary.load(path);
                    nextScopeScripts[alias] = cached.scopeScript || '';
                    const members = filterMembersByPublic(
                        listScopeScriptMembers(cached.scopeScript || ''),
                        cached.scopePublic,
                    ).map((member) => ({
                        ...member,
                        libraryPath: path,
                    }));
                    next[alias] = members;
                }
                catch (e) {
                    utils.warn(`ControlDesignerCodePanel failed to load scope library "${path}"`, e);
                    next[alias] = [];
                    nextScopeScripts[alias] = '';
                }
            }));

            this.libraryMembersByAlias = next;
            this.libraryScopeScriptByAlias = nextScopeScripts;
        },
        async refreshEmbeddedUserControlMembersCache() {
            const refs = listEmbeddedUserControlsInDesign(this.designRoot);
            if (!refs.length) {
                this.embeddedUserControlMembersByName = {};
                this.embeddedUserControlScopeScriptByName = {};
                return;
            }

            const next = {};
            const nextScopeScripts = {};

            await Promise.all(refs.map(async (ref) => {
                const name = String(ref?.name || '').trim();
                const path = String(ref?.controlURL || '').trim();
                if (!name) {
                    return;
                }

                if (!path) {
                    next[name] = [];
                    nextScopeScripts[name] = '';
                    return;
                }

                try {
                    const cached = await utils.scopeLibrary.load(path);
                    nextScopeScripts[name] = cached.scopeScript || '';
                    next[name] = filterMembersByPublic(
                        listScopeScriptMembers(cached.scopeScript || ''),
                        cached.scopePublic,
                    ).map((member) => ({
                        ...member,
                        embeddedControlName: name,
                    }));
                }
                catch (e) {
                    utils.warn(`ControlDesignerCodePanel failed to load embedded UserControl "${path}"`, e);
                    next[name] = [];
                    nextScopeScripts[name] = '';
                }
            }));

            this.embeddedUserControlMembersByName = next;
            this.embeddedUserControlScopeScriptByName = nextScopeScripts;
        },
    },
    render() {
        const panelStyle = {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            flex: 1,
            borderStyle: 'solid',
            borderWidth: '1px',
            boxSizing: 'border-box',
            overflow: 'hidden',
        };

        const toolbarStyle = {
            display: 'flex',
            flexShrink: 0,
            alignItems: 'center',
            padding: '2px 6px',
            minHeight: '26px',
            borderBottom: '1px solid silver',
            backgroundColor: 'silver',
            gap: '6px',
        };

        const publicItems = this.scopeMembers.map((member) => {
            const selected = this.isPublicMemberSelected(member.name);

            return (
                <div
                    key={member.name}
                    class="control-designer-scope-public-item"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '1px 8px',
                        minHeight: '22px',
                        cursor: 'pointer',
                    }}
                    onClick={() => this.togglePublicMember(member.name, !selected)}
                >
                    <QCheckbox
                        dense
                        size="20px"
                        modelValue={selected}
                        onUpdate:modelValue={(value) => this.togglePublicMember(member.name, value)}
                        onClick={(event) => event.stopPropagation()}
                    />
                    <QIcon
                        name={member.kind === 'function' ? 'mdi-function' : 'mdi-variable'}
                        size="12px"
                        color="grey-7"
                        style={{ flexShrink: 0 }}
                    />
                    <span
                        style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '11px',
                            lineHeight: 1.2,
                            color: selected ? '#111827' : '#6b7280',
                        }}
                    >
                        {member.name}
                    </span>
                </div>
            );
        });

        return (
            <div class="control-designer-code-panel" style={panelStyle}>
                <div class="control-designer-code-panel__toolbar" style={toolbarStyle}>
                    <ControlDesignerScopeSummaryField
                        label="Includes"
                        displayText={this.includesDisplayText}
                        popupOpen={this.includesPopupOpen}
                        disableCloseOnApply={this.includesBlockClose}
                        onUpdate:popupOpen={(value) => { this.includesPopupOpen = value; }}
                        onBeforeClose={() => this.onIncludesBeforeClose()}
                    >
                        <ControlDesignerScopeIncludesEditor
                            ref="includesEditor"
                            modelValue={this.scopeIncludes}
                            onUpdate:modelValue={(value) => this.onScopeIncludesChanged(value)}
                            onValidationChange={(errors) => this.onIncludesValidationChange(errors)}
                            onClose={() => this.onIncludesPopupClose()}
                        />
                    </ControlDesignerScopeSummaryField>

                    <ControlDesignerScopeSummaryField
                        label="Public"
                        displayText={this.publicDisplayText}
                        popupOpen={this.publicPopupOpen}
                        popupFit
                        onUpdate:popupOpen={(value) => this.onPublicPopupOpen(value)}
                    >
                        <div
                            class="control-designer-scope-public-menu"
                            style={{ maxHeight: '240px', overflowY: 'auto', padding: '2px 0' }}
                        >
                            {publicItems.length > 0
                                ? publicItems
                                : <div style={{ padding: '4px 8px', fontSize: '11px', color: '#6b7280' }}>No ScopeScript members found.</div>}
                        </div>
                    </ControlDesignerScopeSummaryField>

                    <QBtn
                        flat
                        dense
                        size="xs"
                        padding="none sm"
                        icon="mdi-cog"
                        color="grey-8"
                        title="ScopeScript tools"
                    >
                        <QMenu anchor="bottom right" self="top right">
                            <QList dense style={{ minWidth: '220px' }}>
                                <QItem v-show={this.showLegacyMigrateMenu} clickable onClick={() => this.onMigrateLegacyScopeClick()}>
                                    <QItemSection>
                                        <QItemLabel>Migrate Variables and Functions</QItemLabel>
                                    </QItemSection>
                                </QItem>

                                <QItem clickable onClick={() => this.addOnCreated()}>
                                    <QItemSection>
                                        <QItemLabel>onCreated</QItemLabel>
                                    </QItemSection>
                                </QItem>

                                <QItem clickable onClick={() => this.addOnMounted()}>
                                    <QItemSection>
                                        <QItemLabel>onMounted</QItemLabel>
                                    </QItemSection>
                                </QItem>
                            </QList>
                        </QMenu>
                    </QBtn>
                </div>

                {this.showLegacyCompatibilityWarning ? (
                    <QBanner
                        dense
                        class="control-designer-code-panel__legacy-compatibility-warning"
                        style={{
                            flexShrink: 0,
                            backgroundColor: '#f6ebe1',
                            color: '#d67d26',
                            borderBottom: '1px solid silver',
                        }}
                        v-slots={{
                            avatar: () => <QIcon name="mdi-alert" size="sm" />,
                        }}
                    >
                        Not Vue 2 Portal Compatible! Use Only for Vue 3-Only Pages.
                    </QBanner>
                ) : null}

                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                    {this.showLegacyMigrationGate ? (
                        <div
                            class="control-designer-code-panel__legacy-gate"
                            style={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '12px',
                                padding: '24px',
                                backgroundColor: '#f9fafb',
                                borderTop: '1px solid silver',
                                textAlign: 'center',
                            }}
                        >
                            <div style={{ maxWidth: '420px', fontSize: '12px', lineHeight: 1.5, color: '#6b7280' }}>
                                Legacy Functions/Variables in use. Click Migrate to convert to ScopeScript.
                            </div>
                            <QBtn
                                color="primary"
                                size="sm"
                                label="Migrate"
                                onClick={() => this.onMigrateLegacyScopeClick()}
                            />
                        </div>
                    ) : null}

                    {this.showEditor ? (
                        <MonacoEditor
                            ref="monacoEditor"
                            key={`controlDesignerScopeScriptEditor-${this.mountKey}`}
                            editorId="controlDesignerScopeScriptEditor"
                            lang="control-scopescript"
                            content={this.content}
                            onChange-content={(text) => this.$emit('change-content', text)}
                            onInit={(_editor, monaco) => this.onMonacoInit(_editor, monaco)}
                        />
                    ) : null}
                </div>
            </div>
        );
    },
});
